const APP_ASSET_PATTERN = /(?:^|\/)js\/app\.([a-f0-9]+)\.js(?:[?#]|$)/i
const APP_SCRIPT_PATTERN = /<script\b[^>]*\bsrc=["']([^"']*\/js\/app\.[a-f0-9]+\.js(?:[?#][^"']*)?)["'][^>]*>/i
const VERSION_CHECK_PARAM = '__app_version_check'
const REFRESH_PARAM = '__app_refresh'
const STORAGE_KEY = 'autosquare_app_refresh_version'

let activeVersionCheck = null

export function extractAppVersion(assetUrl) {
  if (typeof assetUrl !== 'string') return null

  const match = assetUrl.match(APP_ASSET_PATTERN)
  return match ? match[1].toLowerCase() : null
}

export function extractAppAssetUrl(html) {
  if (typeof html !== 'string') return null

  const match = html.match(APP_SCRIPT_PATTERN)
  return match ? match[1] : null
}

function findCurrentAppVersion(documentRef) {
  const scripts = Array.from(documentRef.scripts || [])

  for (const script of scripts) {
    const version = extractAppVersion(script.src)
    if (version) return version
  }

  return null
}

function readStoredVersion(sessionStorage) {
  try {
    return sessionStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

function storeVersion(sessionStorage, version) {
  try {
    sessionStorage.setItem(STORAGE_KEY, version)
  } catch {
    // The URL refresh marker still prevents reload loops when storage is unavailable.
  }
}

function clearStoredVersion(sessionStorage) {
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // Storage can be unavailable in privacy modes; version checks should still continue.
  }
}

function removeRefreshParameter(windowRef, currentUrl) {
  if (!currentUrl.searchParams.has(REFRESH_PARAM)) return

  currentUrl.searchParams.delete(REFRESH_PARAM)
  windowRef.history.replaceState(windowRef.history.state, '', currentUrl.toString())
}

export async function refreshIfAppVersionChanged({
  documentRef = document,
  windowRef = window,
  fetchImpl = fetch,
  now = Date.now
} = {}) {
  try {
    const currentVersion = findCurrentAppVersion(documentRef)
    if (!currentVersion) return false

    const checkUrl = new URL('/index.html', windowRef.location.origin)
    checkUrl.searchParams.set(VERSION_CHECK_PARAM, String(now()))
    const response = await fetchImpl(checkUrl.toString(), { cache: 'no-store' })
    if (!response.ok) return false

    const deployedAssetUrl = extractAppAssetUrl(await response.text())
    const deployedVersion = extractAppVersion(deployedAssetUrl)
    if (!deployedVersion) return false

    const currentUrl = new URL(windowRef.location.href)
    if (deployedVersion === currentVersion) {
      clearStoredVersion(windowRef.sessionStorage)
      removeRefreshParameter(windowRef, currentUrl)
      return false
    }

    const refreshMarker = currentUrl.searchParams.get(REFRESH_PARAM)
    const storedVersion = readStoredVersion(windowRef.sessionStorage)
    if (refreshMarker === deployedVersion || storedVersion === deployedVersion) {
      return false
    }

    storeVersion(windowRef.sessionStorage, deployedVersion)
    currentUrl.searchParams.set(REFRESH_PARAM, deployedVersion)
    windowRef.location.replace(currentUrl.toString())
    return true
  } catch {
    // A version check must never prevent the application from starting or resuming.
    return false
  }
}

export function installAppVersionRefresh({
  documentRef = document,
  windowRef = window,
  fetchImpl = fetch
} = {}) {
  const checkVersion = () => {
    if (activeVersionCheck) return activeVersionCheck

    activeVersionCheck = refreshIfAppVersionChanged({
      documentRef,
      windowRef,
      fetchImpl
    }).finally(() => {
      activeVersionCheck = null
    })
    return activeVersionCheck
  }

  windowRef.addEventListener('pageshow', checkVersion)
  documentRef.addEventListener('visibilitychange', () => {
    if (documentRef.visibilityState === 'visible') {
      checkVersion()
    }
  })
  checkVersion()

  return checkVersion
}
